/* jshint esversion: 6 */

import React, { Fragment } from 'react';
import Email from 'react-email-autocomplete';
import PhoneInput from 'react-phone-input-2';
import PropTypes from 'prop-types';
import xss from 'xss';

import ReliableRequest from '../../../lib/ReliableRequest';
import Serialize from '../../../lib/Serialize';
import * as UUID from '../../../lib/uuid';
import { isValidEmail } from '../../../lib/Validate';

import WebsiteWidgetChat from './WebsiteWidgetChat';
import WebsiteWidgetCloseForm from './WebsiteWidgetCloseForm';
import WebsiteWidgetDropdown from './WebsiteWidgetDropdown';

import AvochatoImage from '../../../images/avochato.svg';

class WebsiteWidgetForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      title: 'Chat with us',
      temporaryId: UUID.generate(),
      messages: new Map(),
      status: 'open',
      name: '',
      nameValid: true,
      phone: '+1',
      phoneValid: true,
      email: '',
      emailValid: true,
      timeLeft: 30,
      marketingConsentChecked: false,
    };
    this.muted = false;
    this.lastPhone = '+1';
    this.widgetForm = React.createRef();
    this.websiteWidgetDropdown = React.createRef();
    this.chatInterface = React.createRef();
    this.notificationAudio = React.createRef();
  }

  render() {
    return (
      <form
        id="text_me-form"
        className={this.props.className + ' no-select'}
        ref={this.widgetForm}
        onSubmit={(e) => this.onSubmit(e)}>
        <input type="hidden" name="widget_id" value={this.props.widgetId} />
        <input type="hidden" name="live_session" value={this.props.sessionId || ''} />
        <input type="hidden" name="iframe_domain" value={this.props.iframeDomain} />
        <input type="hidden" name="initial_domain" value={this.props.initialDomain} />
        <input type="hidden" name="temporary_id" value={this.state.temporaryId} />

        {this.renderHeader()}
        {this.renderBody()}
        {this.renderWhitelabel()}
      </form>
    );
  }

  renderHeader() {
    return(
      <Fragment>
        <div id="title">
          <span id="text-me-title-fixed">{this.state.title}</span>
        </div>

        <div id="closer" onClick={() => this.minimizeForm()}>
          <span id="minimize-button">__</span>
          <span id="hard-close-button" onClick={(e) => this.startCloseForm(e)}>×</span>
        </div>
      </Fragment>
    );
  }

  renderBody() {
    // if user is chatting already let them finish chatting before disabling their chat
    if (this.props.disabled && this.props.status === 'qualification') {
      return (
        <div id="text_me-fields">
          <div
            id="disabled_text"
            dangerouslySetInnerHTML={{
              __html: this.props.disabledHTML,
            }} />
        </div>
      );
    }

    switch (this.props.status) {
      case('qualification'):
        return (
          <Fragment>
            {this.renderForm()}
            {this.renderNumber(this.props.showNumber)}
          </Fragment>
        );
      case('chatting'):
        return (
          <Fragment>
            {this.renderCloseForm()}
            {this.renderChat()}
          </Fragment>
        );
      case 'thank_you':
        this.startCountdown();

        return (
          <div id="text_me-fields">
            <div id="thank-you-time-left">Your session will reload in {this.state.timeLeft} second{this.state.timeLeft === 1 ? '' : 's'}</div>
            <div id="thank-you-body">
              Thanks, you should receive a text to "{this.state.phone}" shortly.
            </div>
            {this.renderNumber(this.props.showNumberThankYou)}
          </div>
        );
    }
  }

  renderForm() {
    return (
      <div id="text_me-fields">
        <input type="hidden" name="authenticity_token" value={this.props.authenticityToken} />

        <label htmlFor="name">What&apos;s your name? *</label>
        <input type="text" name="name" id="name"
          className={(this.state.nameValid) ? 'valid' : 'invalid' }
          value={this.state.name}
          onChange={(e) => {
            const {value} = e.target;

            this.setState({
              name: value,
              nameValid: value.length > 0,
            });
          }} />

        <label htmlFor="phone">What is a good number to reach you? *</label>
        <PhoneInput
          inputClass={(this.state.phoneValid) ? 'valid' : 'invalid'}
          country={'us'}
          value={this.state.phone}
          tabIndex={-1}
          countryCodeEditable={true}
          inputProps={{
            name: 'phone',
            required: true,
          }}
          onChange={(phone) => this.onPhoneChange(phone)} />

        {this.renderEmail()}
        {this.renderDropdown()}
        {this.renderMessage()}

        {this.renderDisclaimerSection()}
        {this.renderLiveChatButton()}
        {this.renderTextWithUsButton()}
      </div>
    );
  }
  renderDisclaimerSection() {
    const { consentMode, textWithUsText, disclaimer, marketingConsentText } = this.props;
    const { marketingConsentChecked } = this.state;
    // Marketing widgets: split consent - transactional disclaimer + optional marketing checkbox
    if (consentMode === 'marketing' && textWithUsText) {
      return (
        <div id="disclaimer-container" className="split-consent">
          <div id="disclaimer" dangerouslySetInnerHTML={{
            __html: disclaimer,
          }} />
          <label className="marketing-consent-label">
            <input
              name="marketing_consent"
              type="checkbox"
              checked={marketingConsentChecked}
              onChange={this.handleMarketingConsentCheck}
            />
            <span className="marketing-consent-text">{marketingConsentText}</span>
          </label>
        </div>
      );
    }
    // Transactional widgets: disclaimer text only, no checkbox at all
    if (consentMode === 'transactional' && textWithUsText) {
      return (
        <div id="disclaimer-container" className="transactional-consent">
          <div id="disclaimer" dangerouslySetInnerHTML={{
            __html: disclaimer,
          }} />
        </div>
      );
    }
    // Legacy widgets: original layout - checkbox inline with disclaimer (checkbox gates form)
    return (
      <div id="disclaimer-container" className="legacy-consent">
        {textWithUsText && (
          <input
            name="disclaimerCheck"
            type="checkbox"
            checked={marketingConsentChecked}
            onChange={this.handleMarketingConsentCheck}
          />
        )}
        <div id="disclaimer" dangerouslySetInnerHTML={{
          __html: disclaimer,
        }} />
      </div>
    );
  }

  renderNumber(shouldRender) {
    if (!shouldRender) return null;

    return (
      <Fragment>
        <div className="or-bar">
          <span>or</span>
        </div>
        <div className="text-us">
          Text Us @ <a href={'sms:' + this.props.number}>{this.props.number}</a>
        </div>
      </Fragment>
    );
  }


  renderWhitelabel() {
    if (this.props.whiteLabel) return null;

    return (
      <div id="powered-by" className="no-select">
        Powered by
        <img
          id="logo-img"
          alt="Avochato - Texting for Business"
          src={AvochatoImage} />
      </div>
    );
  }

  renderCloseForm() {
    return (
      <WebsiteWidgetCloseForm
        className={this.state.status === 'closing' ? null : 'hide'}
        onCancel={() => this.setState({status: 'open'})}
        onConfirm={() => this.closeForm(true)}/>
    );
  }

  renderChat() {
    return (
      <Fragment>
        <audio
          ref={this.notificationAudio}
          className="hide"
          src="https://assets.avochato.com/samples/alert_woodblock.mp3"
          type="audio/mp3" />
        <WebsiteWidgetChat
          ref={this.chatInterface}
          className={this.state.status === 'open' ? null : 'hide'}
          chatMessageInputRef={(e) => this.chatMessageTextarea = e}
          sessionId={this.props.sessionId}
          messages={Array.from(this.state.messages.values())}
          onEvent={(e) => this.handleMessageEvents(e)}
          onMissing={() => this.closeForm(false)} />
      </Fragment>
    );
  }

  renderEmail() {
    if (!this.props.emailEnabled) return null;

    return (
      <Email
        className={(this.state.emailValid) ? 'valid' : 'invalid'}
        name="email"
        placeholder="Your Email *"
        domains={[
          'gmail.com',
          'icloud.com',
          'yahoo.com',
          'hotmail.com',
          'me.com',
          'aol.com',
          'mac.com',
          'live.com',
          'googlemail.com',
          'msn.com',
          'facebook.com',
          'verizon.net',
          'outlook.com',
          'rediffmail.com',
          'zohomail.in',
          'zoho.com',
          'mail.com',
          'protonmail.com',
          'yandex.com'
        ]}
        onChange={(e) => this.validateEmail(e)}
        onBlur={(e) => this.validateEmail(e)}
        required />
    );
  }

  validateEmail(event) {
    this.setState({
      email: event.target.value,
      emailValid: isValidEmail(event.target.value),
    });
  }

  renderDropdown() {
    if (!this.props.dropDownEnabled || this.props.dropDownOptions.length === 0) return null;

    return <WebsiteWidgetDropdown ref={this.websiteWidgetDropdown} prompt={this.props.dropDownPrompt} options={this.props.dropDownOptions} />;
  }

  renderMessage() {
    if (!this.props.messageEnabled) return null;

    return (
      <textarea name="help_message" placeholder="How can we help you?" id="help-message" ref={(e) => this.helpMessageTextarea = e} />
    );
  }

  disableEmailButton() {
    const {name, email, emailValid} = this.state;
    if (!this.props.emailEnabled) return false

    return name.length <= 0 || email.length <= 0 || !emailValid;
  }

  renderLiveChatButton() {
    if (!this.props.liveChatText) return null;

    return (
      <input type="submit" name="commit"
        className="submit"
        title={(this.disableEmailButton()) ? 'Please enter a name and email' : null}
        value={this.props.liveChatText}
        disabled={this.disableEmailButton()} />
    );
  }

  disableTextWithUsButton() {
    const {marketingConsentChecked, name, phone} = this.state;
    const strippedPhone = phone.replace(/[^\d]/g, '');
    // Legacy: checkbox gates form — consent required
    // Marketing / Transactional: consent not required (checkbox is optional or absent)
    const consentRequired = this.props.consentMode === 'legacy' && !marketingConsentChecked;
    return consentRequired || name.length <= 0 || strippedPhone.length < 11;
  }

  renderTextWithUsButton() {
    if (!this.props.textWithUsText) return null;

    return (
      <input type="submit" name="commit" className="submit"
        value={this.props.textWithUsText}
        title={(this.disableTextWithUsButton()) ? 'Please enter a name and phone number' : 'Text with us'}
        onClick={(e) => this.textWithUs(e)}
        disabled={this.disableTextWithUsButton()} />
    );
  }

  /* Event handlers */
  startCountdown() {
    if (this.countdown) return;

    this.countdown = setTimeout(() => {
      if (this.state.timeLeft === 0) return this.closeForm(false);

      this.setState({timeLeft: this.state.timeLeft - 1});
      this.countdown = null;
      this.startCountdown();
    }, 1000);
  }

  onPhoneChange(phone) {
    const strippedPhone = phone.replace(/[^\+\d]/g, '');

    // force US phones for now
    const usPhone = strippedPhone.replace(/^\+?1?/, '+1');

    // use same value on a double event (triggered by autofill)
    if (this.lastPhone !== usPhone) {
      this.lastPhone = usPhone;
    }
    return this.setState({
      phone: usPhone,
      phoneValid: usPhone.length >= 12,
    });
  }

  handleMessageEvents(e) {
    switch (e.event) {
      case 'SmsMessage':
      case 'MmsMessage':
        if (e.direction === 'out' && !this.state.messages.has(e.temporary_id)) {
          const now = new Date();
          // only send notification if it's a recent message
          if (now.getTime() - e.sent_at < 5000) this.sendNotification();
        }

        this.state.messages.set(e.temporary_id, {
          type: e.event,
          id: e.temporary_id,
          direction: e.direction,
          messageText: xss(e.message),
          status: 'delivered',
          sentAt: e.sent_at,
        });
        this.setState({messages: this.state.messages});
        break;
      case 'MuteChange':
        this.muted = e.mutedState;
        break;
    }
  }

  sendNotification() {
    if (this.muted) return;

    const {current} = this.notificationAudio;
    if (current) {
      // hack to get browser to not error when sending audio
      current.click();
      current.play();
    }
  }

  minimizeForm() {
    if (typeof(this.props.onMinimize) === 'function') this.props.onMinimize();
  }

  startCloseForm(e) {
    switch(this.props.status) {
      case 'chatting':
        // prevent minimize from triggering and open the closing window instead
        e.stopPropagation();
        this.setState({status: 'closing'});
        break;
      case 'thank_you':
        e.stopPropagation();
        this.closeForm(false);
    }
  }

  closeForm(sendData) {
    this.setState({
      temporaryId: UUID.generate(),
      status: 'open',
      messages: new Map(),
      phone: '+1',
      timeLeft: 30,
    });
    this.lastPhone = '+1';
    if (sendData) {
      ReliableRequest.dispatch({
        url: window.location.pathname,
        method: 'DELETE',
        data: {live_session: this.props.sessionId},
        onSuccess: (e) => {},
      });
    }
    if (typeof(this.props.onClose) === 'function') this.props.onClose();
  }

  onSubmit(e) {
    e.preventDefault();

    const data = Serialize(this.widgetForm.current);
    switch (this.props.status) {
      case('qualification'):
        const helpMessageTextarea = this.helpMessageTextarea;
        let helpMessage = helpMessageTextarea &&
                          helpMessageTextarea.value &&
                          helpMessageTextarea.value.trim();
        if (helpMessageTextarea) helpMessageTextarea.value = '';

        // only append the event if it's not empty
        if (helpMessage) {
          this.state.messages.set(this.state.temporaryId, {
            type: 'SmsMessage',
            id: this.state.temporaryId,
            direction: 'in',
            messageText: xss(helpMessage),
            status: 'queued',
            sentAt: (new Date()).getTime(),
          });
        }

        // always send chat request
        ReliableRequest.dispatch({
          url: window.location.pathname + '/submit',
          method: 'POST',
          data: data,
          onSuccess: (e) => this.props.onStatusChange(e.next_page),
        });

        // try to predict the next state
        this.props.onStatusChange('chatting');
        break;
      case('chatting'):
        const chatMessageTextarea = this.chatMessageTextarea;
        let chatMessage = chatMessageTextarea &&
                          chatMessageTextarea.value &&
                          chatMessageTextarea.value.trim();
        if (chatMessageTextarea) chatMessageTextarea.value = '';

        // don't send any empty chat events (but still clean out the input)
        if (!chatMessage) break;

        this.state.messages.set(this.state.temporaryId, {
          type: 'SmsMessage',
          id: this.state.temporaryId,
          direction: 'in',
          messageText: xss(chatMessage),
          status: 'queued',
          sentAt: (new Date()).getTime(),
        });

        ReliableRequest.dispatch({
          url: window.location.pathname + '/chat',
          method: 'POST',
          data: data,
          onSuccess: () => {},
          onError: () => {
            const {current} = this.chatInterface;
            // fetch the latest state of the ticket
            if (current && current.fetchMissedEvents) current.fetchMissedEvents();
          },
        });
        break;
    }
    this.setState({
      temporaryId: UUID.generate(),
      messages: this.state.messages,
    });
  }

  handleMarketingConsentCheck = ({ target: { checked } }) => this.setState({ marketingConsentChecked: checked });

  textWithUs(e) {
    e.preventDefault();

    const data = Serialize(this.widgetForm.current);

    // send textWithUs request
    ReliableRequest.dispatch({
      url: window.location.pathname + '/text_with_us',
      maxAttempts: 1,
      method: 'POST',
      data: data,
      onSuccess: () => {},
    });

    // just jump to the next state regardless
    this.props.onStatusChange('thank_you');
  }

  setDropDownValue(value) {
    const {current} = this.websiteWidgetDropdown;

    if (current) current.setSelected({target: {value}});
  }
}

WebsiteWidgetForm.propTypes = {
  className: PropTypes.string,
  widgetId: PropTypes.string,
  status: PropTypes.string,
  authenticityToken: PropTypes.string,
  sessionId: PropTypes.string,
  iframeDomain: PropTypes.string,
  initialDomain: PropTypes.string,
  disabled: PropTypes.bool,
  disabledHTML: PropTypes.string,
  emailEnabled: PropTypes.bool,
  dropDownEnabled: PropTypes.bool,
  dropDownPrompt: PropTypes.string,
  dropDownOptions: PropTypes.array,
  messageEnabled: PropTypes.bool,
  disclaimer: PropTypes.string,
  marketingConsentText: PropTypes.string,
  consentMode: PropTypes.string,
  headerText: PropTypes.string,
  liveChatText: PropTypes.string,
  textWithUsText: PropTypes.string,
  showNumber: PropTypes.bool,
  showNumberThankYou: PropTypes.bool,
  number: PropTypes.string,
  whiteLabel: PropTypes.bool,
  onMinimize: PropTypes.func,
  onClose: PropTypes.func,
  onStatusChange: PropTypes.func,
};

export default WebsiteWidgetForm;
